import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { Disclosure } from '../components/Disclosure';
import { formatDateTime } from '../format';
import type { Piece, PieceDetailResponse, PieceRevision, SourceReference } from '../../shared/editorial';
import { contentTypeLabel, isPieceReadOnly } from '../editorial/view';

interface PieceDraft {
  title: string;
  bodyMd: string;
  rationale: string;
  sourceRefs: SourceReference[];
}

type InspectorTab = 'sources' | 'history' | 'preferences' | 'activity';

function toDraft(piece: Piece | PieceRevision): PieceDraft {
  return {
    title: piece.title ?? '',
    bodyMd: piece.bodyMd,
    rationale: piece.rationale ?? '',
    sourceRefs: piece.sourceRefs.map((reference) => ({ ...reference })),
  };
}

function emptyReference(): SourceReference {
  return { sourceType: 'message', sourceId: 0, text: '', role: 'member' };
}

export default function PieceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PieceDetailResponse | null>(null);
  const [draft, setDraft] = useState<PieceDraft | null>(null);
  const [savedDraft, setSavedDraft] = useState<PieceDraft | null>(null);
  const [previewRevision, setPreviewRevision] = useState<PieceRevision | null>(null);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('sources');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const response = await api.get<PieceDetailResponse>(`/editorial/pieces/${id}`);
    setDetail(response);
    const nextDraft = toDraft(response.piece);
    setDraft(nextDraft);
    setSavedDraft(nextDraft);
    setPreviewRevision(null);
    setConflict(false);
  }, [id]);

  useEffect(() => {
    setLoading(true); setError(null); setNotice(null); setDetail(null);
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err))).finally(() => setLoading(false));
  }, [load]);

  const hasRemoteDraft = Boolean(detail?.piece.xArticleId && detail.piece.deliveryStatus !== 'published');
  const readOnly = detail ? isPieceReadOnly(detail.piece) || hasRemoteDraft : true;
  const dirty = Boolean(draft && savedDraft && JSON.stringify(draft) !== JSON.stringify(savedDraft));
  function changeReference(index: number, patch: Partial<SourceReference>) {
    if (!draft) return;
    setDraft({ ...draft, sourceRefs: draft.sourceRefs.map((reference, i) => i === index ? { ...reference, ...patch } : reference) });
  }

  async function save() {
    if (!detail || !draft || readOnly) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const response = await api.patch<{ piece: Piece }>(`/editorial/pieces/${detail.piece.id}`, {
        expectedRevision: detail.piece.revision,
        title: draft.title.trim() || null,
        bodyMd: draft.bodyMd,
        sourceRefs: draft.sourceRefs,
        rationale: draft.rationale.trim() || null,
      });
      await load(); setNotice(`Saved revision ${response.piece.revision}.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) { setConflict(true); setError('A newer revision exists. Your draft is preserved; reload before saving again.'); }
      else setError(err instanceof Error ? err.message : String(err));
    } finally { setSaving(false); }
  }

  async function changeStage(action: 'queue' | 'archive') {
    if (!detail) return;
    setSaving(true); setError(null);
    try {
      await api.post(`/editorial/pieces/${detail.piece.id}/${action}`, { expectedRevision: detail.piece.revision });
      await load(); setNotice(action === 'queue' ? 'Piece queued on this content type’s cadence.' : 'Piece archived.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError('This piece changed elsewhere. Reload before changing its stage.'); else setError(err instanceof Error ? err.message : String(err));
    } finally { setSaving(false); }
  }

  async function startNewEdition() {
    if (!detail) return;
    setSaving(true); setError(null);
    try {
      const response = await api.post<{ piece: Piece }>('/editorial/pieces', {
        profileId: detail.piece.profileId,
        title: detail.piece.title,
        bodyMd: detail.piece.bodyMd,
        sourceRefs: detail.piece.sourceRefs,
        rationale: `New edition of published piece #${detail.piece.id}.`,
      });
      navigate(`/pieces/${response.piece.id}`);
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setSaving(false); }
  }

  if (loading) return <div className="empty">Loading piece…</div>;
  if (!detail || !draft) return <div className="empty"><p>{error ?? 'Piece not found.'}</p><Link to="/dashboard">Back to publications</Link></div>;

  const visibleDocument = previewRevision ? toDraft(previewRevision) : draft;
  const visibleBody = visibleDocument.bodyMd.trim();
  const visibleWordCount = visibleBody ? visibleBody.split(/\s+/).length : 0;
  const isTweet = detail.profile.format === 'tweet';
  const displayTitle = detail.piece.title || (isTweet ? 'Tweet' : `Untitled piece #${detail.piece.id}`);
  const backUrl = `/dashboard?publicationId=${detail.profile.publicationId}&profileId=${detail.profile.id}&tab=content`;

  return (
    <div className="pub-editor">
      <header className="pub-editor-head">
        <div>
          <Link className="pub-editor-back" to={backUrl}>Publications / {contentTypeLabel(detail.profile.format)}</Link>
          <div className="pub-editor-title-row">
            <h1>{displayTitle}</h1>
            <span className={`pub-stage pub-stage-${detail.piece.editorialStage}`}>{detail.piece.editorialStage}</span>
          </div>
          <div className="pub-editor-meta">
            <span>Revision {detail.piece.revision}</span>
            <span>Delivery {detail.piece.deliveryStatus}</span>
            {detail.piece.scheduledFor ? <span>Scheduled {formatDateTime(detail.piece.scheduledFor)}</span> : null}
            {detail.piece.xUrl ? <a href={detail.piece.xUrl} target="_blank" rel="noreferrer">View published piece</a> : null}
          </div>
        </div>
        <div className="pub-editor-head-actions">
          {!readOnly && detail.piece.editorialStage === 'draft' ? <button disabled={saving || dirty} title={dirty ? 'Save changes before queueing.' : undefined} onClick={() => void changeStage('queue')}>Queue</button> : null}
          {!readOnly && detail.piece.editorialStage !== 'archived' ? <button disabled={saving || dirty} title={dirty ? 'Save changes before archiving.' : undefined} onClick={() => void changeStage('archive')}>Archive</button> : null}
          {detail.piece.editorialStage === 'published' ? <button disabled={saving} onClick={() => void startNewEdition()}>New edition</button> : null}
          {!readOnly ? <button className="primary" disabled={saving || !dirty} onClick={() => void save()}>{saving ? 'Saving…' : 'Save revision'}</button> : null}
        </div>
      </header>

      {readOnly ? <p className="editorial-alert">{detail.piece.needsReconciliation ? 'Delivery may have reached its destination. Reconcile the remote receipt before editing or publishing again.' : detail.piece.deliveryStatus === 'publishing' ? 'Delivery is in progress. This revision is locked until it finishes.' : hasRemoteDraft ? 'A remote X draft exists. This revision is locked so local and remote copies cannot drift.' : 'Published editions are immutable snapshots. Start a new edition to develop it further.'}</p> : null}
      {detail.piece.deliveryError ? <p className="editorial-alert fail" role="alert">Delivery error: {detail.piece.deliveryError}</p> : null}
      {error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}
      {notice ? <p className="editorial-alert" role="status">{notice}</p> : null}
      {conflict ? <button onClick={() => { if (window.confirm('Reloading will discard the local draft shown here. Continue?')) void load(); }}>Reload latest revision</button> : null}

      <div className="pub-editor-layout">
        <form className="pub-manuscript" onSubmit={(event) => { event.preventDefault(); void save(); }}>
          {previewRevision ? <div className="pub-revision-banner"><span>Viewing revision {previewRevision.revision}</span><button type="button" onClick={() => setPreviewRevision(null)}>Return to current</button></div> : null}
          {!isTweet ? <label className="pub-title-field"><span className="sr-only">Title</span><input readOnly={readOnly || saving || Boolean(previewRevision)} value={visibleDocument.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder="Untitled" /></label> : null}
          <label className={`pub-body-field${isTweet ? ' pub-tweet-body' : ''}`}><span className="sr-only">{isTweet ? 'Tweet' : 'Body'}</span><textarea readOnly={readOnly || saving || Boolean(previewRevision)} rows={isTweet ? 6 : 24} value={visibleDocument.bodyMd} onChange={(event) => setDraft({ ...draft, bodyMd: event.target.value })} placeholder={isTweet ? 'Write the tweet…' : 'Begin writing…'} /></label>
          <div className="pub-manuscript-measure"><span>{visibleWordCount} words</span><span>{visibleDocument.bodyMd.length} characters</span>{detail.profile.format === 'tweet' ? <span>280 character limit</span> : <span>{detail.profile.maxWords} word limit</span>}</div>

          <Disclosure title="Editorial rationale" meta={visibleDocument.rationale ? 'Included' : 'Optional'} className="pub-rationale">
            <label className="field"><span>Why this version changed</span><textarea readOnly={readOnly || saving || Boolean(previewRevision)} rows={4} value={visibleDocument.rationale} onChange={(event) => setDraft({ ...draft, rationale: event.target.value })} /></label>
          </Disclosure>
          {dirty && detail.piece.editorialStage === 'draft' ? <p className="pub-unsaved">Unsaved changes · save before queueing.</p> : null}
        </form>

        <aside className="pub-inspector">
          <nav className="pub-inspector-tabs" aria-label="Piece details">
            {([
              ['sources', `Sources ${visibleDocument.sourceRefs.length}`],
              ['history', `History ${detail.revisions.length}`],
              ['preferences', `Preferences ${detail.lessons.length}`],
              ['activity', `Activity ${detail.events.length}`],
            ] as [InspectorTab, string][]).map(([value, label]) => <button type="button" key={value} className={inspectorTab === value ? 'active' : ''} aria-pressed={inspectorTab === value} onClick={() => setInspectorTab(value)}>{label}</button>)}
          </nav>

          {inspectorTab === 'sources' ? (
            <section className="pub-inspector-panel">
              <div className="pub-inspector-heading"><div><h2>Sources</h2><p>Original material and research supporting this version.</p></div>{!readOnly && !previewRevision ? <button disabled={saving} type="button" onClick={() => setDraft({ ...draft, sourceRefs: [...draft.sourceRefs, emptyReference()] })}>Add source</button> : null}</div>
              {visibleDocument.sourceRefs.length === 0 ? <div className="pub-inspector-empty">No source references.</div> : null}
              {visibleDocument.sourceRefs.map((reference, index) => (
                <Disclosure key={`${reference.sourceType}-${reference.sourceId}-${index}`} title={`${reference.role === 'member' ? 'Member source' : reference.role === 'quote' ? 'Quotation' : 'Research'} ${index + 1}`} meta={`${reference.sourceType} #${reference.sourceId || '—'}`} className="pub-source" open={index === 0}>
                  <div className="pub-source-grid">
                    <label className="field"><span>Source type</span><select disabled={readOnly || saving || Boolean(previewRevision)} value={reference.sourceType} onChange={(event) => changeReference(index, { sourceType: event.target.value as SourceReference['sourceType'] })}><option value="message">Message</option><option value="media">Library item</option><option value="source_doc">Source document</option></select></label>
                    <label className="field"><span>Source ID</span><input readOnly={readOnly || saving || Boolean(previewRevision)} min={1} type="number" value={reference.sourceId || ''} onChange={(event) => changeReference(index, { sourceId: Number(event.target.value) })} /></label>
                    <label className="field"><span>Role</span><select disabled={readOnly || saving || Boolean(previewRevision)} value={reference.role} onChange={(event) => changeReference(index, { role: event.target.value as SourceReference['role'] })}><option value="member">Member language</option><option value="quote">Quotation</option><option value="research">Research</option></select></label>
                  </div>
                  <label className="field"><span>Source text</span><textarea readOnly={readOnly || saving || Boolean(previewRevision)} rows={6} value={reference.text} onChange={(event) => changeReference(index, { text: event.target.value })} /></label>
                  <label className="field"><span>Note</span><input readOnly={readOnly || saving || Boolean(previewRevision)} value={reference.note ?? ''} onChange={(event) => changeReference(index, { note: event.target.value || undefined })} /></label>
                  {!readOnly && !previewRevision ? <button disabled={saving} type="button" onClick={() => setDraft({ ...draft, sourceRefs: draft.sourceRefs.filter((_, i) => i !== index) })}>Remove source</button> : null}
                </Disclosure>
              ))}
            </section>
          ) : null}

          {inspectorTab === 'history' ? <section className="pub-inspector-panel"><div className="pub-inspector-heading"><div><h2>Revision history</h2><p>Immutable snapshots of this piece.</p></div></div><ol className="pub-ledger">{detail.revisions.map((revision) => <li key={revision.id}><button type="button" className={previewRevision?.id === revision.id ? 'active' : ''} onClick={() => setPreviewRevision(revision)}><strong>Revision {revision.revision}</strong><span>{revision.actor} · {formatDateTime(revision.createdAt)}</span></button></li>)}</ol></section> : null}
          {inspectorTab === 'preferences' ? <section className="pub-inspector-panel"><div className="pub-inspector-heading"><div><h2>Applied preferences</h2><p>Active guidance loaded with this content type.</p></div></div>{detail.lessons.length === 0 ? <div className="pub-inspector-empty">No applied preferences.</div> : <ul className="pub-ledger pub-ledger-plain">{detail.lessons.map((lesson) => <li key={lesson.id}><strong>{lesson.kind === 'preference' ? 'Contextual preference' : lesson.kind}</strong><span>{lesson.category}</span><p>{lesson.text}</p></li>)}</ul>}</section> : null}
          {inspectorTab === 'activity' ? <section className="pub-inspector-panel"><div className="pub-inspector-heading"><div><h2>Activity</h2><p>Concise receipts for editorial changes.</p></div></div>{detail.events.length === 0 ? <div className="pub-inspector-empty">No activity recorded.</div> : <ol className="pub-ledger pub-ledger-plain">{detail.events.map((event) => <li key={event.id}><strong>{event.summary}</strong><span>{event.actor} · {formatDateTime(event.createdAt)}</span></li>)}</ol>}</section> : null}
        </aside>
      </div>
    </div>
  );
}
